import React, { useState } from "react";
import {
  Shield,
  ShieldAlert,
  Camera,
  Mic,
  Eye,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";
import { ProctoringStatus, ProctoringViolation } from "../../hooks/useProctoring";

interface ProctoringBannerProps {
  proctoringStatus: ProctoringStatus;
  onStopProctoring?: () => void;
  showViolationDetails?: boolean;
}

const ProctoringBanner: React.FC<ProctoringBannerProps> = ({
  proctoringStatus,
  onStopProctoring,
  showViolationDetails = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!proctoringStatus.isActive) {
    return null;
  }

  const getViolationTypeLabel = (type: ProctoringViolation["type"]) => {
    switch (type) {
      case "tab_switch":
        return "Tab Switch";
      case "copy_paste":
        return "Copy/Paste";
      case "fullscreen_exit":
        return "Fullscreen Exit";
      case "right_click":
        return "Right Click";
      case "key_combination":
        return "Forbidden Shortcut";
      case "window_blur":
        return "Window Focus Lost";
      default:
        return "Unknown";
    }
  };

  const getStatusColor = () => {
    if (proctoringStatus.violationCount >= 5) return "bg-red-600";
    if (proctoringStatus.violationCount >= 3) return "bg-orange-600";
    if (proctoringStatus.violationCount >= 1) return "bg-yellow-600";
    return "bg-green-600";
  };

  const getStatusText = () => {
    if (proctoringStatus.violationCount >= 5) return "Critical Violations";
    if (proctoringStatus.violationCount >= 3) return "Multiple Violations";
    if (proctoringStatus.violationCount >= 1) return "Violations Detected";
    return "Proctoring Active";
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900 text-white shadow-lg">
      {/* Main Banner */}
      <div className={`px-6 py-3 ${getStatusColor()}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              {proctoringStatus.violationCount > 0 ? (
                <ShieldAlert className="w-5 h-5" />
              ) : (
                <Shield className="w-5 h-5" />
              )}
              <span className="font-semibold">{getStatusText()}</span>
            </div>

            {/* Status Indicators */}
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-1">
                <Eye className="w-4 h-4" />
                <span>
                  {proctoringStatus.windowFocused ? "Focused" : "Unfocused"}
                </span>
              </div>

              {proctoringStatus.cameraStream && (
                <div className="flex items-center space-x-1">
                  <Camera className="w-4 h-4" />
                  <span>Camera Active</span>
                </div>
              )}

              {proctoringStatus.microphoneStream && (
                <div className="flex items-center space-x-1">
                  <Mic className="w-4 h-4" />
                  <span>Mic Active</span>
                </div>
              )}

              {proctoringStatus.violationCount > 0 && (
                <div className="flex items-center space-x-1">
                  <AlertTriangle className="w-4 h-4" />
                  <span>
                    {proctoringStatus.violationCount} Violation
                    {proctoringStatus.violationCount !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {showViolationDetails && proctoringStatus.violations.length > 0 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center space-x-1 hover:bg-black hover:bg-opacity-20 px-2 py-1 rounded"
              >
                <span className="text-sm">Details</span>
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            )}

            {onStopProctoring && (
              <button
                onClick={onStopProctoring}
                className="hover:bg-black hover:bg-opacity-20 p-1 rounded"
                title="Stop Proctoring"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Violation Details */}
      {isExpanded && proctoringStatus.violations.length > 0 && (
        <div className="bg-gray-800 border-t border-gray-700">
          <div className="px-6 py-4">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">
              Recent Violations
            </h3>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {proctoringStatus.violations
                .slice(-5) // Show last 5 violations
                .reverse()
                .map((violation, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between text-xs bg-gray-700 px-3 py-2 rounded"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="font-medium text-red-400">
                        {getViolationTypeLabel(violation.type)}
                      </span>
                      <span className="text-gray-400">
                        {violation.details}
                      </span>
                    </div>
                    <span className="text-gray-500">
                      {violation.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                ))}
            </div>
            {proctoringStatus.violations.length > 5 && (
              <p className="text-xs text-gray-500 mt-2">
                Showing 5 most recent violations out of{" "}
                {proctoringStatus.violations.length} total
              </p>
            )}
          </div>
        </div>
      )}

      {/* Warning Message for High Violations */}
      {proctoringStatus.violationCount >= 3 && (
        <div className="bg-red-700 px-6 py-2 text-center">
          <p className="text-sm font-medium">
            ⚠️ Warning: Multiple violations detected. Your session may be
            terminated if violations continue.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProctoringBanner;